<template>
	<div v-if="slug" data-component="icon" :class="`${size}`">
		<Icon />
	</div>
</template>

<script setup>
import { computed, defineAsyncComponent } from 'vue'

const props = defineProps({
	slug: { type: [Boolean, String], default: false },
	size: {
		type: String,
		default: 'unique',
		validator(value) {
			return ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'].includes(value)
		},
	},
})

const FALLBACK_ICON_SLUG = 'ic-close'
const normalizedSlug = computed(() => (typeof props.slug === 'string' ? props.slug.trim() : ''))

const Icon = defineAsyncComponent(async () => {
	const slug = normalizedSlug.value
	if (!slug) return { render: () => null }

	try {
		return await import(`~/assets/icons/${slug}.svg`)
	} catch {
		try {
			return await import(`~/assets/icons/${FALLBACK_ICON_SLUG}.svg`)
		} catch {
			return { render: () => null }
		}
	}
})
</script>

<style lang="scss" scoped>
[data-component='icon'] {
	position: relative;

	display: flex;
	align-items: center;
	justify-content: center;

	.unique {
		height: rs(16, 16);
		width: rs(16, 16);
	}

	&.xxl {
		height: rs(32, 32);
		width: rs(32, 32);
	}

	&.xl {
		height: rs(42, 42);
		width: rs(42, 42);
	}

	&.lg {
		height: rs(32, 24);
		width: rs(32, 24);
	}

	&.md {
		height: rs(24, 24);
		width: rs(24, 24);
	}

	&.sm {
		height: rs(16, 16);
		width: rs(16, 16);
	}

	&.xs {
		height: rs(12, 10);
		width: rs(12, 10);
	}

	:deep(svg) {
		height: 100%;
		width: 100%;
	}

	:deep(svg) {
		path,
		circle,
		line {
			fill: var(--icon-color, #000000);
			stroke: none;
		}
	}
}
</style>
